/**
 * The URL⇄state boundary for filter values — PURE, no React, no I/O.
 *
 * The URL nests the operator under the filter key, mirroring the platform wire
 * standard (`filters[status][notIn]=A`, see `.claude/rules/query-params.md`);
 * everything downstream — widgets, both folds, the legacy office bridges —
 * works on a flat value map plus a separate operator map. This module is the
 * ONE place that converts between the two, so no other file ever sees the
 * nested form (Rule 19: parse at the boundary, pass the parsed shape down).
 *
 * The field's DEFAULT operator is written BARE (`filters[status]=A`) — the same
 * shorthand the platform accepts for `eq` — so the extra bracket appears only
 * when the user actually picks a non-default operator, and every link minted
 * before operators existed keeps working unchanged.
 *
 * Disambiguation is STRUCTURAL, which is why this needs no field config: a
 * nested map is an operator wrapper iff every key it holds is an operator name.
 * The two object-valued codecs own `start`/`end`/`label`/`min`/`max`, disjoint
 * from the operator vocabulary — that disjointness is what makes
 * `filters[dueDateRange][start]=…` unambiguous, and
 * `__tests__/parse-filter-search.test.ts` fails the build if a future value key
 * ever collides with an operator name.
 */

import type { FilterOp } from "./types";

/**
 * Every operator that may appear as a bracket key. Kept as a value (not just
 * the `FilterOp` type) because the parse is a runtime membership test.
 */
export const FILTER_OPERATOR_KEYS: readonly FilterOp[] = [
  "eq",
  "neq",
  "in",
  "notIn",
  "range",
  "between",
  "gt",
  "gte",
  "lt",
  "lte",
];

/**
 * Keys the object-valued codecs (`dateRangeCodec`, `priceRangeCodec`) put
 * INSIDE a filter value. They must never collide with `FILTER_OPERATOR_KEYS` —
 * a collision would make `filters[dueDateRange][x]` ambiguous between "operator
 * wrapper" and "value field".
 */
export const RANGE_VALUE_KEYS = [
  "start",
  "end",
  "label",
  "min",
  "max",
] as const;

const OPERATOR_KEY_SET: ReadonlySet<string> = new Set(FILTER_OPERATOR_KEYS);

export interface ParsedFilterSearch {
  /** Filter values, keyed by URL filter key — the shape the rest of the system reads. */
  filters: Record<string, unknown>;
  /** Explicitly-chosen operators only; a key absent here runs on its field's default. */
  filtersOp: Record<string, string>;
}

/**
 * A parsed value is an operator wrapper only when EVERY key it carries names an
 * operator. `{ notIn: "a,b" }` is a wrapper; `{ start: "…", end: "…" }` is a
 * date value; `{}` (an object that lost all its keys) is neither, so it stays a
 * value and the codec's `isEmptyValue` skips it.
 */
function isOperatorWrapper(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => OPERATOR_KEY_SET.has(key));
}

/**
 * Split the route's raw `filters` object into values + explicit operators.
 *
 * A hand-edited URL carrying two operators for one key (`filters[k][in]` AND
 * `filters[k][notIn]`) keeps the first — one field has one operator, and
 * `resolveOperator` still degrades anything the field doesn't offer back to its
 * default, so a malformed link renders a working chip rather than throwing.
 */
export function parseFilterSearch(raw: unknown): ParsedFilterSearch {
  const filters: Record<string, unknown> = {};
  const filtersOp: Record<string, string> = {};
  if (!raw || typeof raw !== "object") return { filters, filtersOp };

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isOperatorWrapper(value)) {
      filters[key] = value;
      continue;
    }
    const [operator, inner] = Object.entries(value)[0] as [string, unknown];
    filters[key] = inner;
    filtersOp[key] = operator;
  }
  return { filters, filtersOp };
}

/**
 * The inverse: values + explicit operators → the route's `filters` object.
 * A key with no explicit operator serializes bare, so the URL only grows the
 * operator bracket where the user actually chose one. An `undefined` value
 * stays `undefined` (TanStack drops it from the URL) and takes its operator
 * with it — one key holds both, so a cleared filter can leave no residue.
 */
export function serializeFilterSearch(
  filters: Record<string, unknown>,
  filtersOp: Record<string, string | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    const operator = filtersOp[key];
    out[key] = value === undefined || !operator ? value : { [operator]: value };
  }
  return out;
}
